import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { OrderDetailsTotalStyles } from "@insite/content-library/Widgets/OrderDetails/OrderDetailsTotal";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { OrderDateStyles } from "@WhiteCap/Overrides/Widgets/OrderDetails/OrderDate";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { OrderPoStyles } from "@WhiteCap/Widgets/OrderDetails/OrderDelivery";
import { OrderDetailsInvoiceTableStyles } from "@WhiteCap/Widgets/OrderDetails/OrderDetailsInvoiceTable";
import { OrderQuoteNumberStyles } from "@WhiteCap/Widgets/OrderDetails/OrderQuoteNumber";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css, StyledComponentProps } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;

    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderInvoiceDetailsStyles {
    orderDate?: OrderDateStyles;
    orderQuote?: OrderQuoteNumberStyles;
    orderPO?: OrderPoStyles;
    orderItemCount?: OrderPoStyles;
    orderTotal?: OrderDetailsTotalStyles;
    container?: InjectableCss;
    gridItem?: GridItemProps;
    leftwrapper?: InjectableCss;
    totalwrapper?: InjectableCss;
    invoiceTable?: OrderDetailsInvoiceTableStyles;
    invoiceGridItem?: GridItemProps;
    rightcontainer?: InjectableCss;
    leftcontainer?: InjectableCss;
    mobileLeftcontainer?: InjectableCss;
    orderTotalgridItem?: GridItemProps;
    mobileGridItem?: GridItemProps;
    poMobileGridItem?: GridItemProps;
}

export const invoiceStyles: OrderInvoiceDetailsStyles = {
    container: {
        css: css`
            display: grid;
            grid-template-columns: 71% 30%;
            margin-top: 16px;
        `,
    },
    totalwrapper: {
        css: css`
            display: flex;
            > div[class^="GridItemStyle"] {
                min-width: fit-content;
            }
        `,
    },
    leftwrapper: {
        css: css`
            display: grid;
        `,
    },
    rightcontainer: {
        css: css`
            grid-row: 2;
        `,
    },
    leftcontainer: {
        css: css`
            display: flex;
        `,
    },
    mobileLeftcontainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 6px;
        `,
    },
    gridItem: {
        css: css`
            justify-content: center;
            background-color:${getColor("common.accent")};
            margin-right: 6px !important;
      
             ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css``,
                    css``,
                    css` padding: 6px 12px;`,
                ])}
        `,
    },
    mobileGridItem:{
        css:css` 
                justify-content: center;
                background-color:${getColor("common.accent")};
                padding: 6px 12px;
                margin-top:6px !important;
            `
    },
    poMobileGridItem:{
        css:css`
                justify-content: center;
                background-color:${getColor("common.accent")};
                margin-right: 6px !important;
                margin-top:6px !important;
                padding: 6px 12px;
                flex-basis:54%;
                max-width:54%;
        `
    },
    orderTotalgridItem: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 6px;
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    invoiceGridItem: {
        css: css`
            display: contents;
        `,
    },
    orderQuote: {
        quoteNumber: {
            css: css`
                text-align: center;
            `,
        },
    },
    orderTotal: {
        orderTotal: {
            container: {
                css: css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                max-width: max-content;
                            `,
                            css``,
                            css``,
                            css``,
                            css``,
                        ])}
                `,
            },
        },
    },
};

const styles = invoiceStyles;

const OrderInvoiceDetails: React.FC<WidgetProps> = ({ id }) => {
    return (
        <>
            <Hidden below="md">
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.leftwrapper}>
                        <StyledWrapper {...styles.leftcontainer}>
                            <GridItem {...styles.gridItem} width={3}>
                                <Zone contentId={id} zoneName="Zone0" />
                            </GridItem>
                            <GridItem {...styles.gridItem} width={3}>
                                <Zone contentId={id} zoneName="Zone1" />
                            </GridItem>
                            <GridItem {...styles.gridItem} width={3}>
                                <Zone contentId={id} zoneName="Zone2" />
                            </GridItem>
                            <GridItem {...styles.gridItem} width={3}>
                                <Zone contentId={id} zoneName="Zone3" />
                            </GridItem>
                        </StyledWrapper>
                        <StyledWrapper {...styles.rightcontainer}>
                            <GridItem {...styles.invoiceGridItem}>
                                <Zone contentId={id} zoneName="Zone4" />
                            </GridItem>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.totalwrapper} className="wrapper-right">
                        <GridItem {...styles.gridItem} width={6}>
                            <Zone contentId={id} zoneName="Zone5" />
                        </GridItem>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
            <Hidden above="sm">
                <StyledWrapper>
                    <StyledWrapper {...styles.leftwrapper}>
                        <StyledWrapper {...styles.mobileLeftcontainer}>
                            <GridItem {...styles.poMobileGridItem} width={6}>
                                <Zone contentId={id} zoneName="Zone-M0" />
                            </GridItem>
                            <GridItem {...styles.mobileGridItem} width={5}>
                                <Zone contentId={id} zoneName="Zone-M1" />
                            </GridItem>
                            <GridItem {...styles.poMobileGridItem} width={6}>
                                <Zone contentId={id} zoneName="Zone-M2" />
                            </GridItem>
                            <GridItem {...styles.mobileGridItem} width={5}>
                                <Zone contentId={id} zoneName="Zone-M3" />
                            </GridItem>
                        </StyledWrapper>
                        <StyledWrapper {...styles.rightcontainer}>
                            <GridItem {...styles.invoiceGridItem}>
                                <Zone contentId={id} zoneName="Zone-M4" />
                            </GridItem>
                            <GridItem {...styles.orderTotalgridItem} width={12}>
                                <Zone contentId={id} zoneName="Zone-M5" />
                            </GridItem>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderInvoiceDetails),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
    },
};

export default widgetModule;
